<script setup lang="ts">
import { onMounted, Ref, ref, watch, computed } from 'vue';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkUserUpdatePermission } from '@ilteducation/customer-mgmt-permissions';

import useModal from '../../composables/useModal';
import { useLocale } from '../../composables/useLocale';
import { usePermissions } from '../../composables/usePermissions';
import Modal from '../Modal.vue';
import CreateLoginCard from './CreateLoginCard.vue';

const { t } = useLocale();

type Props = {
  name: 'CREATE_LOGIN_CARD';
  user: CustomerMgmtTypes.GetUserResponse;
};

type Emits = {
  userUpdated: [];
};
const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { isOpen, close, context: contentProps } = useModal(props.name);
const {
  permissionContext,
  getCallerAssociations,
  getGroupMemberships,
  createGetTargetAssociations,
  reporterWithReason,
} = usePermissions();

const updateUserAndDownload = async () => {
  isUpdating.value = true;
  await loginCard.value.updateUserLoginInfo().then(async (success: boolean) => {
    if (success) {
      updated.value = true;
      await downloadLoginCard();
    }
  });

  emit('userUpdated');
  isUpdating.value = false;
};

const downloadLoginCard = async () => {
  await loginCard.value.downloadLoginCard().catch((error: unknown) => {
    console.error('Failed to save the login card', error);
  });
};

const closeAndReset = () => {
  updated.value = false;

  isUpdating.value = false;
  gotoStepOne();
  close();
};

const loginCard = ref();
const isUpdating = ref(false);
const updated = ref(false);

const action = ref(updateUserAndDownload);

const showCancelAction = ref(true);
const cancelAction = ref(closeAndReset);

const secondaryAction: Ref<(() => void) | null> = ref(null);

const isCreateLoginCardDisabled = computed(() => {
  return (
    isOpen &&
    (isUpdating.value ||
      updated.value ||
      (qrTokenEditableCheck.value.allowed === false && passwordEditableCheck.value.allowed === false) ||
      loginCard.value?.createLoginCardDisabled)
  );
});

const step = ref(1);

const gotoStepOne = () => {
  step.value = 1;
  isUpdating.value = false;
  updated.value = false;

  action.value = updateUserAndDownload;

  showCancelAction.value = true;
  cancelAction.value = closeAndReset;

  secondaryAction.value = null;
};

const gotoStepTwo = () => {
  step.value = 2;
  isUpdating.value = false;

  action.value = async () => closeAndReset();

  showCancelAction.value = false;
  cancelAction.value = closeAndReset;

  secondaryAction.value = downloadLoginCard;
};

const checkUserPropertiesEditable = (
  user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput,
  propertiesToCheck: Partial<CustomerMgmtTypes.UpdateUserRequest>,
): Promise<ReturnType<typeof reporterWithReason>> => {
  if (!user) {
    return Promise.resolve({ allowed: false, reason: 'not_permitted' });
  }

  return checkUserUpdatePermission(
    permissionContext.value,
    {
      userId: user.uid,
      ...propertiesToCheck,
    },
    getCallerAssociations,
    createGetTargetAssociations(user),
    getGroupMemberships,
    reporterWithReason,
  );
};
const qrTokenEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
const passwordEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
watch(
  isOpen,
  async (newIsOpen) => {
    if (newIsOpen) {
      qrTokenEditableCheck.value = await checkUserPropertiesEditable(props.user, { qrToken: 'fake_value' });
      passwordEditableCheck.value = await checkUserPropertiesEditable(props.user, { password: 'fake_value' });
    }
  },
  { immediate: true },
);

const showCancelButton = computed(() => {
  return showCancelAction.value && !contentProps.value.hideBackButton;
});

watch(updated, (userUpdated) => {
  if (userUpdated === true) {
    gotoStepTwo();
  }
});

const title = computed(() =>
  step.value === 1 ? t('lcp.create_new_login_card') : t('lcp.login_card_created_successfully'),
);

const secondaryActionLabel = computed(() => (step.value === 1 ? '' : t('lcp.download_login_card')));
const actionLabel = computed(() => (step.value === 1 ? t('lcp.create_and_download') : t('lcp.close')));

onMounted(() => {
  gotoStepOne();
});
</script>

<template>
  <Modal
    :name="name"
    :title="title"
    :is-open="isOpen"
    :show-close-btn="true"
    :close-btn-action="closeAndReset"
    :action="action"
    :action-label="actionLabel"
    :show-cancel-action="showCancelButton"
    :cancel-action="cancelAction"
    :cancel-action-label="t('lcp.back')"
    :action-disabled="step === 1 ? isCreateLoginCardDisabled : false"
    :secondary-action="secondaryAction ?? undefined"
    :secondary-action-label="secondaryActionLabel"
    :loading="isUpdating"
  >
    <CreateLoginCard
      v-if="isOpen"
      ref="loginCard"
      :user="user"
      :apps="[]"
      app-url=""
      :create-qr-code-check="contentProps.createQrCodeCheck"
      :qrTokenDisabled="qrTokenEditableCheck.allowed ? false : (qrTokenEditableCheck.reason as string)"
      :isPasswordDisabled="passwordEditableCheck.allowed ? false : (passwordEditableCheck.reason as string)"
    />
  </Modal>
</template>
